import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Kicker } from "@/components/ui/kicker";
import { TerminalBlock } from "@/components/ui/terminal-block";
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Bot,
  CheckCircle2,
  Lock,
  Terminal,
} from "lucide-react";
import { cn } from "@/lib/utils";

const STEPS = [
  {
    num: "1",
    title: "Queue in Cloud",
    icon: ArrowDownCircle,
    summary:
      "When a finding is triaged to Ready to fix or a verification is requested, a work request enters the project's work pool.",
    detail:
      "Work requests are durable queue items in the database. Nothing is pushed to your laptop immediately.",
  },
  {
    num: "2",
    title: "Pull Down Locally",
    icon: Terminal,
    summary:
      "You or your local AI coding agent pull the queued tasks down into your workspace.",
    detail:
      "Run iamu pull in your terminal, or ask Claude Code / Cursor to check pending work via the iamu_requests MCP tool.",
  },
  {
    num: "3",
    title: "2-Hour Lease Lock",
    icon: Lock,
    summary:
      "When an agent begins work, it claims a 2-hour exclusive lease lock on the request.",
    detail:
      "This prevents teammates' agents or automated CI pools from duplicating effort or generating conflicting branches.",
  },
  {
    num: "4",
    title: "Verify Closure & Sync",
    icon: ArrowUpCircle,
    summary:
      "The agent implements the fix, re-runs the scenario walkthrough to verify closure, and pushes results.",
    detail:
      "Only a re-run can transition a problem to verified. Run iamu sync to publish the resolution back to the cloud.",
  },
];

export function SyncWorkflowGuide() {
  const [activeTab, setActiveTab] = useState<"cli" | "agent">("cli");

  return (
    <Card className="border-border">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-2">
          <Bot className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <Kicker as="h3">Pull-based architecture</Kicker>
        </div>
        <CardTitle className="text-base font-semibold tracking-tight text-foreground">
          How local synchronization and agent execution work
        </CardTitle>
        <CardDescription className="text-xs text-muted-foreground max-w-2xl">
          Iamu never pushes code or commands to developer machines. All fixes,
          scenarios, and verification runs are pulled locally by developers and
          their AI coding agents.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Step cards */}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((step) => {
            const Icon = step.icon;
            return (
              <div
                key={step.num}
                className="rounded-lg bg-muted/30 p-3.5 space-y-2 flex flex-col justify-between"
              >
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-foreground text-background text-2xs font-semibold">
                      {step.num}
                    </span>
                    <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                  </div>
                  <h4 className="text-xs font-semibold text-foreground pt-0.5">
                    {step.title}
                  </h4>
                  <p className="text-2xs leading-relaxed text-muted-foreground">
                    {step.summary}
                  </p>
                </div>
                <p className="text-3xs text-muted-foreground border-t border-border/50 pt-2 font-mono">
                  {step.detail}
                </p>
              </div>
            );
          })}
        </div>

        {/* Command tabs */}
        <div className="space-y-3 pt-1">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-semibold text-foreground">
              Pull commands & agent prompts
            </h4>
            <div className="flex items-center rounded-md border border-border bg-muted/40 p-0.5 text-2xs">
              <button
                type="button"
                onClick={() => setActiveTab("cli")}
                className={cn(
                  "px-2.5 py-1 rounded font-medium transition-colors",
                  activeTab === "cli"
                    ? "bg-foreground text-background font-semibold shadow-xs"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                Terminal CLI
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("agent")}
                className={cn(
                  "px-2.5 py-1 rounded font-medium transition-colors",
                  activeTab === "agent"
                    ? "bg-foreground text-background font-semibold shadow-xs"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                AI Coding Agent
              </button>
            </div>
          </div>

          {activeTab === "cli" ? (
            <TerminalBlock
              label="Local terminal"
              lines={[
                "# 1. Pull pending work requests, scenarios, and findings from cloud",
                "npx @iamu-dev/iamu pull",
                "",
                "# 2. Re-run verification walkthrough after fixing in code",
                "npx @iamu-dev/iamu run",
                "",
                "# 3. Push verified resolutions and updated findings back to cloud",
                "npx @iamu-dev/iamu sync",
              ]}
              copyText="npx @iamu-dev/iamu pull"
              copyLabel="Copy iamu pull command"
            />
          ) : (
            <TerminalBlock
              label="Claude Code / Cursor Prompt"
              lines={[
                "# In Claude Code, Cursor, or Windsurf (with iamu-sync MCP connected):",
                'Ask: "Check pending Iamu work requests and fix the highest priority finding"',
                "",
                "# The agent calls iamu_requests to list items and iamu_requests_claim to lock it,",
                "# implements the code changes, runs the verification scenario, and marks it resolved.",
              ]}
              copyText="Check pending Iamu work requests and fix the highest priority finding"
              copyLabel="Copy agent prompt"
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}
